<template>
    <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
            <div class="modal-header">
                <h2>Change Password</h2>
                <button class="close-button" @click="closeModal">
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <div class="modal-body">
                <form @submit.prevent="changePassword">
                    <div class="form-group">
                        <label for="currentPassword">Current Password</label>
                        <input id="currentPassword" v-model="form.currentPassword" type="password"
                            placeholder="Enter current password" required class="form-control" />
                        <span v-if="errors.currentPassword" class="error-text">{{ errors.currentPassword }}</span>
                    </div>

                    <div class="form-group">
                        <label for="newPassword">New Password</label>
                        <input id="newPassword" v-model="form.newPassword" type="password"
                            placeholder="Enter new password" required class="form-control" />
                        <span v-if="errors.newPassword" class="error-text">{{ errors.newPassword }}</span>
                    </div>

                    <div class="form-group">
                        <label for="confirmPassword">Confirm Password</label>
                        <input id="confirmPassword" v-model="form.confirmPassword" type="password"
                            placeholder="Confirm new password" required class="form-control" />
                        <span v-if="errors.confirmPassword" class="error-text">{{ errors.confirmPassword }}</span>
                    </div>

                    <div v-if="successMessage" class="success-message">
                        {{ successMessage }}
                    </div>
                    <div v-if="errorMessage" class="error-message">
                        {{ errorMessage }}
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn-cancel" @click="closeModal">Cancel</button>
                        <button type="submit" class="btn-submit" :disabled="isLoading">
                            <span v-if="isLoading">
                                <i class="fas fa-spinner fa-spin"></i> Changing...
                            </span>
                            <span v-else>Change Password</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ChangePasswordModal',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            form: {
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            },
            errors: {
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            },
            isLoading: false,
            errorMessage: '',
            successMessage: '',
        };
    },
    methods: {
        async changePassword() {
            // Reset messages
            this.errorMessage = '';
            this.successMessage = '';
            this.errors = {
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            };

            // Validation
            if (!this.form.currentPassword) {
                this.errors.currentPassword = 'Current password is required';
                return;
            }

            if (!this.form.newPassword) {
                this.errors.newPassword = 'New password is required';
                return;
            }

            if (this.form.newPassword.length < 4) {
                this.errors.newPassword = 'Password must be at least 4 characters';
                return;
            }

            if (this.form.newPassword !== this.form.confirmPassword) {
                this.errors.confirmPassword = 'Passwords do not match';
                return;
            }

            if (this.form.currentPassword === this.form.newPassword) {
                this.errors.newPassword = 'New password must be different from current password';
                return;
            }

            // Call API
            this.isLoading = true;
            try {
                const token = localStorage.getItem('access_token');
                
                const payload = {
                    oldpassword: this.form.currentPassword,
                    newpassword: this.form.newPassword,
                };
                
                const data = new FormData();
                data.append('transaction', JSON.stringify(payload));
                
                const config = {
                    headers: {
                        'accept': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                };
                
                //console.log('Sending request with payload:', payload);
                const response = await axios.post('change_password', data, config);

                // Handle API response format: [status_code, message, data]
                const [statusCode, message] = response.data;

                if (statusCode === 1) {
                    // Success
                    this.successMessage = message || 'Password changed successfully!';
                    
                    // Emit event to parent to handle logout and redirect
                    setTimeout(() => {
                        this.resetForm();
                        this.$emit('close');
                        this.$emit('passwordChangeSuccess');
                    }, 2000);
                } else {
                    // Error
                    this.errorMessage = message || 'An error occurred while changing password.';
                }
            } catch (error) {
                console.error('Error changing password:', error);

                if (error.response?.data) {
                    // Handle API response format: [status_code, message, data]
                    const [statusCode, message] = error.response.data;
                    if (statusCode === 0) {
                        this.errors.currentPassword = message || 'Current password is incorrect.';
                    } else {
                        this.errorMessage = message || 'An error occurred while changing password. Please try again.';
                    }
                } else if (error.response?.status === 401) {
                    this.errors.currentPassword = 'Current password is incorrect';
                } else {
                    this.errorMessage = error.message || 'An error occurred while changing password. Please try again.';
                }
            } finally {
                this.isLoading = false;
            }
        },

        closeModal() {
            this.resetForm();
            this.$emit('close');
        },

        resetForm() {
            this.form = {
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            };
            this.errors = {
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            };
            this.errorMessage = '';
            this.successMessage = '';
        },
    },
};
</script>

<style scoped lang="scss">
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.modal-content {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 450px;
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 20px;
    border-bottom: 1px solid #e0e0e0;
    background-color: #f5f5f5;

    h2 {
        margin: 0;
        font-size: 20px;
        color: #333;
    }

    .close-button {
        background: none;
        border: none;
        font-size: 24px;
        color: #666;
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
            color: #333;
        }
    }
}

.modal-body {
    padding: 20px;
}

.form-group {
    label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #333;
        font-size: 14px;
    }

    .form-control {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        transition: border-color 0.2s;

        &:focus {
            outline: none;
            border-color: #8697c4;
            box-shadow: 0 0 0 2px rgba(134, 151, 196, 0.1);
        }
    }

    .error-text {
        display: block;
        color: #d32f2f;
        font-size: 12px;
        margin-top: 4px;
    }
}

.success-message {
    padding: 12px;
    margin-bottom: 15px;
    background-color: #e8f5e9;
    color: #2e7d32;
    border-radius: 4px;
    border-left: 4px solid #2e7d32;
    font-size: 14px;
}

.error-message {
    padding: 12px;
    margin-bottom: 15px;
    background-color: #ffebee;
    color: #c62828;
    border-radius: 4px;
    border-left: 4px solid #c62828;
    font-size: 14px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 15px 20px;
    border-top: 1px solid #e0e0e0;
    background-color: #f5f5f5;
}

.btn-cancel,
.btn-submit {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.btn-cancel {
    background-color: #e0e0e0;
    color: #333;

    &:hover:not(:disabled) {
        background-color: #d0d0d0;
    }
}

.btn-submit {
    background-color: #3259bb;
    color: white;

    &:hover:not(:disabled) {
        background-color: #254496;
    }

    &:active:not(:disabled) {
        transform: translateY(1px);
    }
}
</style>
